import { Box, Button, Card, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionInvoiceResponse } from '../../../services/subscriptionPaymentService';
import { useAppSelector } from '../../../store/store';
import { formatSubscriptionDate, formatSubscriptionPrice } from '../../../utils/subscriptionFormatting';

const formatInvoiceAmount = (value?: number | null, currency?: string | null) => {
    return formatSubscriptionPrice(value, currency) ?? '-';
};

const formatInvoiceDate = (value?: string | null) => {
    return formatSubscriptionDate(value) ?? '-';
};

const getStatusChipColors = (status?: string | null) => {
    const normalized = (status || '').toLowerCase();
    if (normalized.includes('paid') || normalized.includes('success')) {
        return {
            backgroundColor: 'var(--button-background-color)',
            color: 'var(--button-text-color)',
        };
    }

    if (normalized.includes('open') || normalized.includes('draft') || normalized.includes('pending')) {
        return {
            borderColor: 'var(--selected-table-row-color)',
            color: 'var(--link-text-color)',
        };
    }

    return {
        borderColor: 'var(--selected-table-row-color)',
        color: 'var(--link-text-color)',
    };
};

interface InvoicesSectionProps {
    showHeader?: boolean;
}

export function InvoicesSection({ showHeader = true }: InvoicesSectionProps) {
    const { t } = useTranslation();
    const { invoices, loadingInvoices } = useAppSelector((state) => state.subscription);

    const invoiceColumns = useMemo<GridColDef<SubscriptionInvoiceResponse>[]>(
        () => [
            {
                field: 'invoiceNumber',
                headerName: t('PROFILE_INVOICES_NUMBER'),
                minWidth: 150,
                flex: 1,
                valueGetter: (params) => params.row.invoiceNumber || params.row.stripeInvoiceId || '-',
            },
            {
                field: 'status',
                headerName: t('PROFILE_INVOICES_STATUS'),
                minWidth: 130,
                flex: 0.8,
                renderCell: (params) => (
                    <Chip
                        size="small"
                        label={params.row.status ? t(params.row.status) : '-'}
                        variant="outlined"
                        sx={{
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            height: 26,
                            fontSize: '0.75rem',
                            ...getStatusChipColors(params.row.status),
                        }}
                    />
                ),
            },
            {
                field: 'createdUtc',
                headerName: t('PROFILE_INVOICES_CREATED'),
                minWidth: 120,
                flex: 0.8,
                valueFormatter: (params) => formatInvoiceDate(params.value as string | null),
            },
            {
                field: 'dueDateUtc',
                headerName: t('PROFILE_INVOICES_DUE'),
                minWidth: 120,
                flex: 0.8,
                valueFormatter: (params) => formatInvoiceDate(params.value as string | null),
            },
            {
                field: 'paidAtUtc',
                headerName: t('PROFILE_INVOICES_PAID'),
                minWidth: 120,
                flex: 0.8,
                valueFormatter: (params) => formatInvoiceDate(params.value as string | null),
            },
            {
                field: 'amountDue',
                headerName: t('PROFILE_INVOICES_AMOUNT_DUE'),
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatInvoiceAmount(params.row.amountDue, params.row.currency)}
                    </Typography>
                ),
            },
            {
                field: 'amountPaid',
                headerName: t('PROFILE_INVOICES_AMOUNT_PAID'),
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatInvoiceAmount(params.row.amountPaid, params.row.currency)}
                    </Typography>
                ),
            },
            {
                field: 'actions',
                headerName: t('PROFILE_INVOICES_ACTIONS'),
                minWidth: 220,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1}>
                        {params.row.hostedInvoiceUrl && (
                            <Button
                                className="button-color"
                                size="small"
                                variant="contained"
                                sx={{
                                    minWidth: 70,
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    height: 32,
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        borderWidth: 1.5,
                                    },
                                }}
                                onClick={() => window.open(params.row.hostedInvoiceUrl || '', '_blank', 'noopener,noreferrer')}
                            >
                                {t('PROFILE_INVOICES_OPEN')}
                            </Button>
                        )}
                        {params.row.invoicePdfUrl && (
                            <Button
                                className="button-color"
                                size="small"
                                variant="contained"
                                sx={{
                                    minWidth: 64,
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    height: 32,
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        borderWidth: 1.5,
                                    },
                                }}
                                onClick={() => window.open(params.row.invoicePdfUrl || '', '_blank', 'noopener,noreferrer')}
                            >
                                {t('PROFILE_INVOICES_PDF')}
                            </Button>
                        )}
                        {!params.row.hostedInvoiceUrl && !params.row.invoicePdfUrl && (
                            <Typography variant="body2" sx={{ color: 'var(--link-text-color)' }}>
                                -
                            </Typography>
                        )}
                    </Stack>
                ),
            },
        ],
        [t]
    );

    return (
        <Card
            variant="outlined"
            sx={{
                borderColor: 'var(--selected-table-row-color)',
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
        >
            {showHeader && (
                <Box
                    sx={{
                        px: { xs: 1.5, md: 2.5 },
                        py: { xs: 1, md: 1.5 },
                        borderBottom: '1px solid var(--selected-table-row-color)',
                        backgroundColor: 'action.hover',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                        {t('PROFILE_INVOICES_TITLE')}
                    </Typography>
                </Box>
            )}

            <Box sx={{ p: { xs: 1, md: 1.5 } }}>
                {loadingInvoices ? (
                    <Stack spacing={1.8} alignItems="center" sx={{ py: 5 }}>
                        <CircularProgress size={36} sx={{ color: 'var(--button-background-color)' }} />
                        <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 600 }}>
                            {t('PROFILE_LOADING_SUBSCRIPTION_DETAILS')}
                        </Typography>
                    </Stack>
                ) : invoices.length === 0 ? (
                    <Box
                        sx={{
                            py: 6,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: '1px dashed var(--selected-table-row-color)',
                            backgroundColor: 'action.hover',
                        }}
                    >
                        <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 500, fontSize: '0.95rem' }}>
                            {t('PROFILE_INVOICES_EMPTY')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <DataGrid
                            autoHeight
                            rows={invoices}
                            columns={invoiceColumns}
                            getRowId={(row) => row.stripeInvoiceId}
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                                pagination: {
                                    paginationModel: { page: 0, pageSize: 10 },
                                },
                            }}
                            sx={{
                                borderColor: 'var(--selected-table-row-color)',
                                borderRadius: 2.5,
                                overflow: 'hidden',
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'var(--card-header-background-color)',
                                    color: 'var(--card-header-text-color)',
                                    borderBottom: '2px solid var(--selected-table-row-color)',
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                                    fontSize: '0.875rem',
                                },
                                '& .MuiDataGrid-row': {
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'var(--selected-table-row-color)',
                                    },
                                },
                                '& .MuiDataGrid-row:nth-of-type(even)': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    borderTop: '2px solid var(--selected-table-row-color)',
                                    backgroundColor: 'action.hover',
                                    minHeight: 56,
                                },
                                '& .MuiTablePagination-root': {
                                    color: 'var(--link-text-color)',
                                },
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Card>
    );
}